import { useCallback } from "react";

import type { WebInvoiceDraft, WebInvoiceLine } from "../lib/invoices";
import { newInvoiceLine } from "../lib/invoiceEditor";
import { BLOCK_KIND } from "../lib/invoiceBlocks";

/**
 * Die Schreiboperationen auf `draft.line_rows` an einer Stelle.
 *
 * Aus `InvoicePositionEditor` herausgelöst, damit der über der
 * 300-Zeilen-Grenze aus den Code-Standards blieb. Alle arbeiten auf einer
 * **Kopie** der Liste — die Zeilen sind Zustand, und ein `splice` auf dem
 * Original änderte ihn an React vorbei.
 */
export function useBelegZeilen(
  draft: WebInvoiceDraft,
  onChange: (draft: WebInvoiceDraft) => void,
) {
  const lines = draft.line_rows;

  const setzen = useCallback(
    (neu: WebInvoiceLine[]) => onChange({ ...draft, line_rows: neu }),
    [draft, onChange],
  );

  const anhaengen = useCallback(
    (neu: WebInvoiceLine[]) => setzen([...lines, ...neu]),
    [lines, setzen],
  );

  /**
   * Zeilen an einer bestimmten Stelle einsetzen.
   *
   * Nur ans Ende anhängen zu können hieß: eine vergessene Position musste man
   * unten anlegen und dann Schritt für Schritt hochklicken.
   */
  const einfuegenBei = useCallback((stelle: number, neu: WebInvoiceLine[]) => {
    const kopie = [...lines];
    kopie.splice(stelle, 0, ...neu);
    setzen(kopie);
  }, [lines, setzen]);

  const aendern = useCallback((index: number, patch: Partial<WebInvoiceLine>) => {
    const kopie = [...lines];
    kopie[index] = { ...kopie[index], ...patch };
    setzen(kopie);
  }, [lines, setzen]);

  /** Eine Zeile um eins bewegen — der kleine Weg neben der Gliederung. */
  const verschieben = useCallback((index: number, richtung: -1 | 1) => {
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= lines.length) return;
    // Eine Position darf ihren Blockkopf nicht überspringen — sie landete
    // sonst still im Nachbarblock und damit in dessen Zwischensumme. Wer sie
    // dorthin haben will, zieht sie in der Gliederung.
    if (lines[ziel].line_kind === BLOCK_KIND) return;
    const kopie = [...lines];
    [kopie[index], kopie[ziel]] = [kopie[ziel], kopie[index]];
    setzen(kopie);
  }, [lines, setzen]);

  /** Entfernen. Die letzte Zeile wird durch eine leere ersetzt, nicht gelöscht —
   *  ein Beleg ganz ohne Positionszeile hätte kein Eingabefeld mehr. */
  const entfernen = useCallback((index: number) => {
    const rest = lines.filter((_, i) => i !== index);
    setzen(rest.length ? rest : [newInvoiceLine()]);
  }, [lines, setzen]);

  const duplizieren = useCallback((index: number) => {
    const kopie = [...lines];
    kopie.splice(index + 1, 0, { ...kopie[index], id: newInvoiceLine().id });
    setzen(kopie);
  }, [lines, setzen]);

  /** Eine Zeile durch mehrere ersetzen — für den Baustein aus der Bibliothek. */
  const ersetzen = useCallback((index: number, neu: WebInvoiceLine[]) => {
    const kopie = [...lines];
    kopie.splice(index, 1, ...neu);
    setzen(kopie);
  }, [lines, setzen]);

  return { setzen, anhaengen, einfuegenBei, aendern, verschieben, entfernen, duplizieren, ersetzen };
}
